import React, { useLayoutEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core';
import checkerboardImage from '../../../../../ui/src/assets/images/checkerboard.svg?url';
import { useSurfaceClasses } from '../../../components/Surface/Surface';
import { colorArrayToDataUrl } from '../../../utils/colorArrayToDataUrl';
import { Typography } from '../Typography';
import Loader from '../Loader/Loader';

const styles = (theme: Theme) =>
  createStyles({
    mobile: {},
    root: {
      height: '100%',
      position: 'relative',
      '&$mobile': {
        position: 'unset'
      }
    },
    success: {
      // backgroundColor: theme.customPalette.success,
      '& $image': {
        filter: 'blur(0px)',
        transition: 'filter 0.5s ease'
      }
    },
    failed: {
      // backgroundColor: theme.customPalette.dangerError,
      '&$mobile': {
        // height: '30vh',
        minHeight: 'calc(30vh - 56px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }
    },
    loading: {
      // backgroundColor: theme.customPalette.backgroundAccent.whiteAccent1,
      '& $image': {
        filter: 'blur(5px)'
      }
    },
    image: {
      display: 'block',
      maxHeight: '100%',
      maxWidth: '100%',
      objectFit: 'contain',
      '$mobile &': {
        width: '100vw',
        maxHeight: '83vh'
      }
    },
    fullSize: {
      maxHeight: 'none',
      maxWidth: 'none'
    },
    checkerboard: {
      background: `url('${checkerboardImage}')`,
      '$mobile &': {
        background: 'unset',
        backgroundColor: theme.customPalette.surface.shade2
      }
    },

    spinner: {},
    error: {},
    centered: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
      '$mobile &': {
        position: 'unset',
        top: 'unset',
        left: 'unset',
        transform: 'unset'
        // top: 'calc(50% - 38px)' // minus controls bar
      }
    }
  });

export interface ImageLoaderProps {
  src: string;
  background?: string;
  fallback?: string | JSX.Element;
  alt?: string;
  placeholder?: string;
  dimensions?: { width: string | number; height: string | number };
  fullSize?: boolean;
  floatingEffect?: boolean;
  /** override styles for the `img` */
  className?: string;
  peekHeader?: string;
  /** examples:
 * - `[
      [[0,0,0], [0,128,128], [0,0,255]],
      [[0,255,0], [128,128,0], [255,0,0]]
    ]`
    - `rgba(125,125,125,0.4)`
 */
  peekPlaceholder?: string | [];
  peekUrl?: string;
  borderRadius?: number;
}

const ImageLoader = React.forwardRef<
  HTMLImageElement,
  ImageLoaderProps & WithStyles<typeof styles>
>(
  (
    {
      src,
      alt,
      fallback = undefined,
      peekHeader = 'Image-Peek',
      peekPlaceholder = undefined,
      classes,
      className = '',
      background = '',
      peekUrl = undefined,
      dimensions = { width: undefined, height: undefined },
      fullSize = false,
      floatingEffect = false,
      borderRadius
    }: ImageLoaderProps & WithStyles<typeof styles>,
    ref
  ) => {
    const backgroundClass =
      background === 'checkerboard' ? classes.checkerboard : '';
    const [success, setSuccess] = useState(false);
    const [failed, setFailed] = useState(false);
    const [peek, setPeek] = useState(undefined);

    // classes for when floating effect is true: corner radius, borders and elevation
    const floatingEffectClasses = useSurfaceClasses({
      floating: true,
      corners: 'rounded',
      floatingHeight: 2,
      cornerRadius: 'lowEmphasis'
    });

    useLayoutEffect(() => {
      setPeek(undefined);

      if (!src) {
        setSuccess(false);
        setFailed(false);
        return;
      }

      if (peekPlaceholder) {
        setPeek(colorArrayToDataUrl(peekPlaceholder));
      }

      if (peekUrl) {
        setPeek(peekUrl);
      }

      new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = resolve;
        image.onerror = reject;
        image.src = src;
        if (image.complete) {
          setSuccess(true);
          setFailed(false);
        } else {
          setSuccess(false);
          setFailed(false);
        }
      })
        .then(() => {
          setSuccess(true);
          setFailed(false);
        })
        .catch(() => {
          setSuccess(false);
          setFailed(true);
        });
    }, [peekPlaceholder, peekUrl, src]);

    // sometimes the dimensions from `poster` !== the image's dimension, then the image will be stretched.
    const dim = useRef({
      width: isMobile ? '' : 'auto',
      height: dimensions.height,
      borderRadius: borderRadius ?? 'unset'
    }).current;

    if (success) {
      return (
        <div
          key={src}
          className={`${classes.root} ${classes.success} ${
            floatingEffect ? floatingEffectClasses : ''
          } ${isMobile ? classes.mobile : ''}`}
        >
          <img
            ref={ref}
            key="image"
            className={[
              className || classes.image,
              backgroundClass,
              fullSize ? classes.fullSize : '',
              className
            ].join(' ')}
            src={src}
            alt={alt}
            style={dim}
          />
        </div>
      );
    }

    if (failed || !src) {
      return (
        <div
          key={src}
          className={`${className} ${classes.root} ${classes.failed} ${
            isMobile ? classes.mobile : ''
          }`}
        >
          {fallback &&
            (typeof fallback === 'string' ? (
              <img className={classes.image} src={fallback} alt={alt} />
            ) : (
              fallback
            ))}
          {!fallback && (
            <div className={classes.centered}>
              <Typography>No image available</Typography>
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        key={src}
        className={[className, classes.root, classes.loading].join(' ')}
        style={dim}
      >
        {peek ? (
          <img
            key="image"
            className={[
              classes.image,
              backgroundClass,
              fullSize ? classes.fullSize : ''
            ].join(' ')}
            src={peek}
            alt={alt}
            style={dim}
          />
        ) : null}
        <div className={`${classes.spinner} ${classes.centered}`}>
          <Loader variant="indeterminate" />
        </div>
      </div>
    );
  }
);

export default withStyles(styles)(ImageLoader);
